import { camelCase, mapKeys, noop, snakeCase } from 'lodash';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { dialogTypes } from '@/constants/alertTypes';
import { NEW_VIEW } from '@/constants/dashboardConstants';
import alertsUtil from '@/util/alertsUtil';
import { useCurrentUserContext } from '@shared/hooks/useCurrentUserContext';
import { ProjectTemplate } from '@experiment-management-shared/types';
import { projectViewsStore } from '@shared';
import { APIError } from '@shared/api';
import api from '../api';
import { useGenerateUnsavedViewId } from './useGenerateUnsavedViewTemplateId';
import useProject from './useProject';

const SNAKE_CASE_KEYS = [
  'createdBy',
  'createdFromTemplateId',
  'currentProjectId',
  'projectDefault',
  'projectId',
  'templateId',
  'templateName',
  'unsavedView',
  'lastUpdate'
];

const getLocalViewId = (view: ProjectTemplate) => {
  let viewId = view.templateId ?? view.createdFromTemplateId ?? '';

  if (view.v3?.isPersonalView) {
    viewId = viewId || NEW_VIEW;
  }

  return viewId;
};

const normalizeView = (view: ProjectTemplate) => {
  return mapKeys(view, (value, key) => {
    if (SNAKE_CASE_KEYS.includes(key)) return snakeCase(key);

    return key;
  });
};

const patchCreatedFromTemplateId = (
  createdFromTemplateId: string | null,
  unsavedTemplateId: string
) => {
  return createdFromTemplateId && createdFromTemplateId !== NEW_VIEW
    ? createdFromTemplateId
    : unsavedTemplateId;
};

const upsertView = async ({
  projectId,
  view,
  unsavedTemplateId,
  userName
}: {
  projectId: string;
  view: ProjectTemplate;
  unsavedTemplateId: string;
  userName: string;
}) => {
  return api.post('dashboard-templates/project/upsert', {
    current_project_id: projectId,
    ...normalizeView({
      ...view,
      // force sending `unsavedTemplateId` when creating a new template from the personal view
      createdFromTemplateId: patchCreatedFromTemplateId(
        view.createdFromTemplateId,
        unsavedTemplateId
      ),
      v3: { ...view.v3, lastUpdatedBy: userName }
    })
  });
};

// do not use if user has no access!
export default function useUpsertViewMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data: project } = useProject();
  const { user: userData } = useCurrentUserContext();
  const unsavedTemplateId = useGenerateUnsavedViewId();
  const userName = userData?.username || '';
  const projectId = project?.projectId as string;

  return useMutation(
    ({ view }: { view: ProjectTemplate }) => {
      if (!project.canEdit) return Promise.resolve();

      return upsertView({
        projectId,
        view,
        unsavedTemplateId: unsavedTemplateId || '',
        userName
      });
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(['projectViews', { projectId }]);
      },
      onError: (error: APIError) => {
        const msg = error.response?.data.msg ?? '';

        dispatch(
          alertsUtil.openErrorDialog(
            dialogTypes.CATCH_ERROR_SAVE_TEMPLATE,
            `There was an error saving your view: ${msg}`
          )
        );
      },
      onSuccess: async (savedServerTemplate, { view }) => {
        // in case of guest users it'll resolve with `undefined`
        if (!savedServerTemplate) return;

        // @todo: move to a helper with the implementation from `useProjectViews`
        const savedTemplate = mapKeys(savedServerTemplate, (_, key) =>
          camelCase(key)
        ) as ProjectTemplate;

        const templateId = savedTemplate.templateId;

        queryClient.setQueryData<ProjectTemplate[] | undefined>(
          ['projectViews', { projectId }],
          templates => {
            if (!templates) return templates;

            return [
              savedTemplate,
              ...templates.filter(template => {
                // remove saved template from templates (update case)
                if (template.templateId === savedTemplate.templateId) {
                  return false;
                }

                // keep rest of saved views
                if (!template.unsavedView) return true;

                // replicate BE logic to remove unsaved view
                // if it was an update, we should keep
                if (view.templateId) return true;

                // if it was a new template and created from unsaved view we should remove
                return (
                  savedTemplate.createdFromTemplateId !== unsavedTemplateId
                );
              })
            ];
          }
        );

        queryClient.setQueryData<ProjectTemplate | undefined>(
          ['projectViewDetails', { projectId, templateId }],
          () => {
            return {
              ...savedTemplate
            };
          }
        );

        // remove from local IndexedDB only after success + refetch
        const localViewId = getLocalViewId(view);
        await projectViewsStore.delete([projectId, localViewId]).catch(noop);
        // remove personal view if exists
        if (savedTemplate.createdFromTemplateId === unsavedTemplateId) {
          await projectViewsStore.delete([projectId, NEW_VIEW]).catch(noop);
        }

        queryClient.invalidateQueries(['projectViews', { projectId }]);
        queryClient.invalidateQueries([
          'projectViewDetails',
          { projectId, templateId }
        ]);
      }
    }
  );
}
